import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, LogOut } from "lucide-react";
import { useB2BTranslation } from "../../B2BShared/hooks/useB2BTranslation";
import { useB2BAuth } from "../../B2BShared";
import { API_CONFIG } from "../../../../utils/apiConfig";
import "../../B2BShared/B2BButtonStyles/B2BButtonStyles.css";
import "./B2BWaitingApproval.css";

const B2BWaitingApproval: React.FC = () => {
  const { t, tAuth } = useB2BTranslation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useB2BAuth();
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);


  // Check approval status immediately and periodically
  useEffect(() => {
    const checkApprovalStatus = async () => {
      const token = localStorage.getItem("b2bToken") || user?.token;

      if (!token) {
        console.log("No token available for approval status check");
        return;
      }

      try {
        const response = await fetch(
          `${API_CONFIG.B2B_API_URL}/api/b2b/auth/me`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const userData = await response.json();
          console.log("User status check:", userData);

          // Update localStorage with fresh user data
          if (userData.data?.user) {
            localStorage.setItem("b2bUser", JSON.stringify(userData.data.user));
          }

          // If user is now approved, redirect to dashboard immediately
          if (userData.data?.user?.isApproved === true) {
            console.log("User approved! Redirecting to dashboard...");
            navigate("/gcb/dashboard", { replace: true });
            return;
          }
        } else if (response.status === 429) {
          console.warn("Rate limited - will retry later");
          // Don't retry immediately on rate limiting
          return;
        } else {
          console.error("Failed to check approval status:", response.status);
        }
      } catch (error) {
        console.error("Error checking approval status:", error);
      }
    };

    // Check immediately
    checkApprovalStatus();

    // Then check every 2 minutes (reduced API load)
    const interval = setInterval(checkApprovalStatus, 120000);

    return () => clearInterval(interval);
  }, [user?.token, navigate]);

  // Also check approval status from user context or localStorage on mount
  useEffect(() => {
    // Check if user is already approved from context or localStorage
    let isUserApproved = user?.isApproved;
    
    if (isUserApproved === undefined) {
      const storedUser = localStorage.getItem("b2bUser");
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          isUserApproved = parsedUser?.isApproved;
        } catch (e) {
          console.error("Error parsing stored user:", e);
        }
      }
    }

    // If user is already approved, redirect immediately
    if (isUserApproved === true) {
      console.log("User is already approved, redirecting to dashboard...");
      navigate("/gcb/dashboard", { replace: true });
    }
  }, [user?.isApproved, navigate]);

  // Redirect if not authenticated (but allow if there's a token in localStorage - pending approval case)
  useEffect(() => {
    // Don't redirect if user is logging out
    if (isLoggingOut) {
      return;
    }
    
    const hasToken = localStorage.getItem("b2bToken");
    if (!isAuthenticated && !hasToken) {
      console.log("No authentication and no token - redirecting to login");
      navigate("/gcb/login", { replace: true });
    } else if (hasToken && !isAuthenticated) {
      console.log("Has token but not authenticated - likely pending approval case, staying on page");
    }
  }, [isAuthenticated, navigate, isLoggingOut]);

  const handleLogout = () => {
    setIsLoggingOut(true);
    // Navigate to main B2B page first
    navigate("/gcb", { replace: true });
    // Then logout (this will clear auth state)
    logout();
  };



  // Show loading or return null only if neither authenticated nor has token
  const hasToken = localStorage.getItem("b2bToken");
  const storedUser = localStorage.getItem("b2bUser");
  
  if (!isAuthenticated && !hasToken) {
    return null;
  }

  // Check if user's email is verified before showing waiting approval
  let userEmailVerified = false;
  let isUserApproved = false;
  
  if (user?.isEmailVerified) {
    userEmailVerified = true;
  } else if (storedUser) {
    try {
      const parsedUser = JSON.parse(storedUser);
      userEmailVerified = parsedUser?.isEmailVerified || false;
      isUserApproved = parsedUser?.isApproved || false;
    } catch (e) {
      console.error("Error parsing stored user:", e);
    }
  }

  // Also check from user context
  if (user?.isApproved) {
    isUserApproved = true;
  }

  // If user is already approved, redirect to dashboard
  if (isUserApproved) {
    console.log("User is already approved, redirecting to dashboard");
    navigate("/gcb/dashboard", { replace: true });
    return null;
  }

  // If email is not verified, redirect to email verification
  if (!userEmailVerified) {
    console.log("Email not verified, redirecting to email verification");
    navigate("/gcb/email-verification");
    return null;
  }

  return (
    <div className="waiting-approval-container">
      <div className="waiting-approval-content">
        <div className="waiting-approval-header">
          <div className="waiting-icon">
            <Clock size={48} />
          </div>
          {/* Personalized greeting */}
          {(() => {
            // Try to get name from user context or localStorage
            let userName = user?.name || user?.fullName;
            
            if (!userName && storedUser) {
              try {
                const parsedUser = JSON.parse(storedUser);
                userName = parsedUser?.name || parsedUser?.fullName;
              } catch (e) {
                console.error("Error parsing stored user for greeting:", e);
              }
            }
            
            return userName ? (
              <div className="personal-greeting">
                <p className="greeting-text">
                  {t("dashboard.waitingApproval.greeting", { name: userName })}
                </p>
              </div>
            ) : null;
          })()}
          <h1>
            {t("dashboard.waitingApproval.title")}
          </h1>
          <p className="waiting-subtitle">
            {t("dashboard.waitingApproval.subtitle")}
          </p>
          <div className="review-message-box">
            <p className="review-message">
              {t("dashboard.waitingApproval.reviewMessage")}
            </p>
          </div>
        </div>

        {/* <div className="waiting-approval-steps">
          <div className="step completed">
            <div className="step-icon">
              <Mail size={20} />
            </div>
            <div className="step-content">
              <h3>{t("dashboard.waitingApproval.step1Title")}</h3>
              <p>{t("dashboard.waitingApproval.step1Description")}</p>
            </div>
          </div>

          <div className="step current">
            <div className="step-icon">
              <Clock size={20} />
            </div>
            <div className="step-content">
              <h3>{t("dashboard.waitingApproval.step2Title")}</h3>
              <p>{t("dashboard.waitingApproval.step2Description")}</p>
            </div>
          </div>

          <div className="step pending">
            <div className="step-icon">
              <Users size={20} />
            </div>
            <div className="step-content">
              <h3>{t("dashboard.waitingApproval.step3Title")}</h3>
              <p>{t("dashboard.waitingApproval.step3Description")}</p>
            </div>
          </div>
        </div> */}


        <div className="waiting-approval-footer">
          <p>{t("dashboard.waitingApproval.helpDescription")}</p>
          <div className="footer-actions">
            <a 
              href="mailto:info@globalchic.club" 
              className="contact-link"
            >
              {t("dashboard.waitingApproval.contactAdmin")}
            </a>
            <button 
              onClick={handleLogout}
              className="logout-button"
            >
              <LogOut size={16} />
              {tAuth("logout")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default B2BWaitingApproval;
